import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { useGetLeetcodeDataLazyQuery } from "../../../graphql/generated.tsx";
import { extractLeetCodeUsername, validateLeetCodeUsername } from "../../../utils/leetcode";
import {
  useGetCampusQuery,
  useGetStudentByEmailIdQuery,
  useUpdateStudentMutation,
} from "../../../graphql/generated.tsx";
import { Alert } from "flowbite-react";
import StudentLayout from "../Layout/index.jsx";
import NetworkIndicator from "../../Practice/Components/NetworkIndicator.jsx";
import GuardedActionButton from "../../Practice/Components/GuardedActionButton.jsx";
import ButtonLoader from "../../Practice/Components/ButtonLoader.jsx";

const MyProfile = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const isCompletionRequired = searchParams.get("complete") === "required";
  const email = JSON.parse(localStorage.getItem("user")).email;

  const {
    data: StudentData,
    error,
    refetch: refetchStudent,
    loading: studentLoading,
  } = useGetStudentByEmailIdQuery({
    variables: { email },
    fetchPolicy: "network-only",
  });

  const { data: CampusData } = useGetCampusQuery({
    variables: { id: StudentData?.getStudentByEmailId?.campusId },
    skip: !StudentData,
  });

  const [formData, setFormData] = useState({
    dob: "",
    hackerRankUname: "",
    id: "",
    leetCodeUname: "",
    name: "",
    phno: "",
    placeOfResidence: "",
    email: "",
    rollNo: "",
    department: "",
    campusName: "",
    personalEmail: "",
  });

  const [isEditing, setIsEditing] = useState(isCompletionRequired);
  const [loadingFields, setLoadingFields] = useState(true);
  const [updateStudent] = useUpdateStudentMutation();
  const [validateLeetCode] = useGetLeetcodeDataLazyQuery();
  const [leetCodeError, setLeetCodeError] = useState("");
  const [formErrors, setFormErrors] = useState({});
  const [isLeetCodeValidating, setIsLeetCodeValidating] = useState(false);

  const validatePhoneNumber = (phone) => /^\d{10}$/.test((phone || "").trim());

  useEffect(() => {
    
    setLoadingFields(true);
    refetchStudent && refetchStudent().catch(() => {}).finally(() => setLoadingFields(false));
  }, []);

  useEffect(() => {
    if (StudentData?.getStudentByEmailId) {
      setFormData({
        dob: StudentData.getStudentByEmailId.dob || "",
        hackerRankUname: StudentData.getStudentByEmailId.hackerRankUname || "",
        id: StudentData.getStudentByEmailId.id || "",
        leetCodeUname: StudentData.getStudentByEmailId.leetCodeUname || "",
        name: StudentData.getStudentByEmailId.name || "",
        phno: StudentData.getStudentByEmailId.phno || "",
        placeOfResidence: StudentData.getStudentByEmailId.placeOfResidence || "",
        email: StudentData.getStudentByEmailId.email || "",
        rollNo: StudentData.getStudentByEmailId.rollNo || "",
        department: StudentData.getStudentByEmailId.department || "",
        campusName: CampusData?.campus?.name || "",
        personalEmail: StudentData.getStudentByEmailId.personalEmail || "",
        personalId: StudentData.getStudentByEmailId.personalId || "",
      });
    }
  }, [
    CampusData?.campus?.name,
    StudentData?.getStudentByEmailId?.dob,
    StudentData?.getStudentByEmailId?.hackerRankUname,
    StudentData?.getStudentByEmailId?.id,
    StudentData?.getStudentByEmailId?.leetCodeUname,
    StudentData?.getStudentByEmailId?.name,
    StudentData?.getStudentByEmailId?.phno,
    StudentData?.getStudentByEmailId?.placeOfResidence,
    StudentData?.getStudentByEmailId?.email,
    StudentData?.getStudentByEmailId?.rollNo,
    StudentData?.getStudentByEmailId?.department,
    StudentData?.getStudentByEmailId?.personalEmail,
    StudentData?.getStudentByEmailId?.personalId,
    StudentData,
  ]);

  const handleSave = async () => {
    setLeetCodeError("");
    let errors = {};
    // List of required fields (except campusName and email)
    const requiredFields = [
      "dob",
      "department",
      "hackerRankUname",
      "id",
      "leetCodeUname",
      "name",
      "phno",
      "placeOfResidence",
      "rollNo"
    ];
    requiredFields.forEach((field) => {
      if (!formData[field] || formData[field].trim() === "") {
        errors[field] = `${field === "leetCodeUname" ? "LeetCode Username" : field.charAt(0).toUpperCase() + field.slice(1).replace(/([A-Z])/g, ' $1').replace(/Uname/, ' Username')} is required`;
      }
    });
    setFormErrors(errors);
    // Department minimum length check (only if not empty)
    if (formData.department && formData.department.trim() !== "" && formData.department.length < 2) {
      errors.department = "Minimum 2 characters required.";
      setFormErrors(errors);
      return;
    }

    if (!validatePhoneNumber(formData.phno)) {
      errors.phno = "Phone Number must be exactly 10 digits.";
      setFormErrors(errors);
      return;
    }

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    // LeetCode username validation
    const leetCodeInput = formData.leetCodeUname?.trim();
    setIsLeetCodeValidating(true);
    const extracted = extractLeetCodeUsername(leetCodeInput);
    if (!extracted) {
      setLeetCodeError("Could not extract a valid LeetCode username from the input.");
      setIsLeetCodeValidating(false);
      return;
    }
    const { valid, error } = await validateLeetCodeUsername(validateLeetCode, extracted);
    if (!valid) {
      setLeetCodeError(error);
      setIsLeetCodeValidating(false);
      return;
    }
    // Set the extracted username before saving
    formData.leetCodeUname = extracted;
    setLeetCodeError("");
    setIsLeetCodeValidating(false);
    setIsEditing(false);
    const { campusName, personalId, ...studentInput } = formData;
    setFormData((prev) => ({ ...prev, ...studentInput }));
    setLoadingFields(true);
    await updateStudent({
      variables: {
        updateStudentInput: studentInput,
      },
      refetchQueries: ["GetStudentByEmailId"],
      awaitRefetchQueries: true,
    });
    await refetchStudent();
    setLoadingFields(false);
    handleSuccessfulSave();
  };

  const handleCancel = () => {
    if (StudentData?.getStudentByEmailId) {
      setFormData({
        dob: StudentData.getStudentByEmailId.dob || "",
        hackerRankUname: StudentData.getStudentByEmailId.hackerRankUname || "",
        id: StudentData.getStudentByEmailId.id || "",
        leetCodeUname: StudentData.getStudentByEmailId.leetCodeUname || "",
        name: StudentData.getStudentByEmailId.name || "",
        phno: StudentData.getStudentByEmailId.phno || "",
        placeOfResidence: StudentData.getStudentByEmailId.placeOfResidence || "",
        email: StudentData.getStudentByEmailId.email || "",
        rollNo: StudentData.getStudentByEmailId.rollNo || "",
        department: StudentData.getStudentByEmailId.department || "",
        campusName: CampusData?.campus?.name || "",
        personalEmail: StudentData.getStudentByEmailId.personalEmail || "",
        personalId: StudentData.getStudentByEmailId.personalId || "",
      });
    }
    setIsEditing(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    // Department: only allow letters, numbers, and hyphens
    if (name === "department") {
      const validPattern = /^[a-zA-Z0-9-]*$/;
      if (value !== "" && !validPattern.test(value)) {
        if (/\s/.test(value)) {
          setFormErrors((prev) => ({ ...prev, department: "Spaces not allowed. Use '-' instead (eg: CSE, AIDS-A, MECH)" }));
        } else {
          setFormErrors((prev) => ({ ...prev, department: "Special characters not allowed. Only letters, numbers, and '-' are permitted." }));
        }
        return; // Block the input
      }
    }

    if (name === "phno") {
      const digitsOnly = value.replace(/\D/g, "").slice(0, 10);
      setFormData((prevData) => ({
        ...prevData,
        phno: digitsOnly,
      }));
      setFormErrors((prev) => ({
        ...prev,
        phno:
          digitsOnly.length === 0 || digitsOnly.length === 10
            ? undefined
            : "Phone Number must be exactly 10 digits.",
      }));
      return;
    }

    setFormData((prevData) => ({
      ...prevData,
      [name]: name === "department" ? value.toUpperCase() : value,
    }));
    // Clear error for the field as user types
    setFormErrors((prev) => {
      const updated = { ...prev, [name]: undefined };
      if (name === "department" && value.trim() !== "" && value.length > 0 && value.length < 2) {
        updated.department = "Minimum 2 characters required.";
      }
      return updated;
    });
    if (name === "leetCodeUname") {
      setLeetCodeError("");
    }
  };

  const renderField = (field, label, editable = true) => (
    <div key={field} className="flex flex-col gap-1">
      <label className="text-sm font-semibold text-gray-700">{label}</label>
      {isEditing && editable ? (
        <>
          <input
            type={field === "dob" ? "date" : "text"}
            name={field}
            value={formData[field]}
            onChange={handleInputChange}
            placeholder={`Enter ${label}`}
            inputMode={field === "phno" ? "numeric" : undefined}
            maxLength={field === "phno" ? 10 : undefined}
            className={`p-3 border ${formErrors[field] || (field === "leetCodeUname" && isCompletionRequired && (!formData.leetCodeUname || formData.leetCodeUname.length < 5 || formData.leetCodeUname.length > 40)) || (field === "department" && isCompletionRequired && !formData.department) ? "border-red-500" : "border-gray-200"} bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200 transition w-full min-w-[200px]`}
            style={{ minWidth: "200px" }}
          />
          {(formErrors[field] || (field === "leetCodeUname" && leetCodeError)) && (
            <span className="mt-1 text-sm text-red-500">{formErrors[field] || leetCodeError}</span>
          )}
          {field === "leetCodeUname" && isCompletionRequired && !formErrors[field] && !leetCodeError && (!formData.leetCodeUname || formData.leetCodeUname.length < 3 || formData.leetCodeUname.length > 40) && (
            <span className="mt-1 text-sm text-red-500">Incomplete or incorrect LeetCode username</span>
          )}
          {field === "department" && isCompletionRequired && !formErrors[field] && !formData.department && (
            <span className="mt-1 text-sm text-red-500">Department is required to complete your profile</span>
          )}
        </>
      ) : loadingFields ? (
        <span className="block w-full h-[44px] bg-gray-200 rounded animate-pulse" />
      ) : (
        <p className="p-3 text-gray-900 bg-gray-100 rounded-md min-w-[200px]">
          {formData[field] || "-"}
        </p>
      )}
    </div>
  );

  if (error) {
    const raw = error?.graphQLErrors?.[0]?.message || error?.message || "";
    const isContextCause = raw.includes(":") && !raw.includes(" ");
    const errorMessage = isContextCause
      ? raw.split(":")[1].replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
      : raw || "Error loading profile. Please try again later.";
    return (
      <StudentLayout>
        <div className="flex items-center justify-center min-h-screen">
          <div className="inline-flex">
            <Alert color="failure">
              <span>{errorMessage}</span>
            </Alert>
          </div>
        </div>
      </StudentLayout>
    );
  }

  // Clear the completion param after successful save
  const handleSuccessfulSave = () => {
    if (isCompletionRequired) {
      setSearchParams({});
    }
  };

  return (
    <StudentLayout>
      <NetworkIndicator />
      <div className="flex flex-col w-full gap-2 px-4 py-2 mx-auto mt-4 bg-white rounded-lg shadow-lg max-w-7xl sm:px-6 sm:py-3 sm:mt-6 md:px-8 md:py-4 md:mt-8">
      
        <div
          className="w-full mb-2 rounded-t-lg"
          style={{ background: "linear-gradient(to right, #0e3d5d, #e1effe)" }}
        >
          <div className="flex items-center h-12 px-4 sm:px-8">
            <span className="text-lg font-semibold tracking-wide text-white">
              My Profile
            </span>
          </div>
        </div>
        <div className="flex flex-col items-center w-full gap-2 px-4 pb-2 mb-2 sm:flex-row sm:px-6">
          <div className="flex items-center flex-shrink-0 pr-3 mb-2 sm:mb-0">
            <img
              src={process.env.PUBLIC_URL + "/images/users/myprofile.png"}
              alt="User Icon"
              className="object-cover rounded-full shadow h-11 w-11"
              style={{ minWidth: "48px", minHeight: "48px" }}
            />
          </div>
          <div className="flex flex-col justify-center w-full">
            <div className="flex flex-col w-full gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex flex-col gap-1 w-full">
                <div className="flex items-center gap-3 flex-wrap">
                  {isEditing ? (
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Enter Name"
                      className="w-full max-w-xs p-2 text-xl font-bold text-gray-800 transition border border-gray-200 rounded-md bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      style={{ minWidth: "120px" }}
                    />
                  ) : loadingFields ? (
                    <span className="block w-1/2 h-6 bg-gray-200 rounded animate-pulse" />
                  ) : (
                    <span className="text-xl font-bold text-gray-800">
                      {formData.name || "-"}
                    </span>
                  )}
                </div>
                {loadingFields ? (
                  <span className="block w-1/3 h-4 bg-gray-200 rounded animate-pulse" />
                ) : (
                  <span className="text-sm text-gray-500">{formData.email || "-"}</span>
                )}
              </div>
              <div className="flex items-center mt-2 sm:mt-0 sm:ml-4">
                {!isEditing ? (
                  <GuardedActionButton
                    onAction={() => setIsEditing(true)}
                    label="Edit"
                    className="px-5 py-1.5 text-base font-semibold text-white transition rounded-md hover:bg-slate-800 bg-[#1e293b]"
                    spinnerOnChecking={false}
                    disabled={loadingFields}
                  />
                ) : (
                  <div className="flex gap-1">
                    <button
                      onClick={handleSave}
                      className="px-4 py-1.5 text-base font-semibold text-white transition rounded-md bg-[#1e293b] hover:bg-slate-800"
                      disabled={isLeetCodeValidating}
                    >
                      {isLeetCodeValidating ? "Validating..." : "Apply"}
                    </button>
                    <button
                      onClick={handleCancel}
                      className="px-4 py-1.5 text-base font-semibold text-white transition rounded-md bg-[#1e293b] hover:bg-slate-800"
                      disabled={isLeetCodeValidating}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        <div className="grid w-full grid-cols-1 gap-2 px-3 sm:grid-cols-2 sm:px-4">
          {renderField("rollNo", "Roll No")}
          {renderField("campusName", "College Name")}
          {renderField("department", "Department")}
          {renderField("dob", "Date of Birth")}
          {renderField("hackerRankUname", "HackerRank Profile")}
          {renderField("leetCodeUname", "LeetCode Profile")}
          {renderField("phno", "Phone Number")}
          {renderField("placeOfResidence", "Place of Residence")}
        </div>
        <div className="grid w-full grid-cols-1 gap-4 px-3 mt-2 sm:grid-cols-2 sm:px-4 sm:mt-4">
          <div className="flex flex-col items-start w-full">
            <span className="mb-1 text-sm font-semibold text-gray-500">
              Personal Email
            </span>
            {loadingFields ? (
              <span className="block w-full h-5 bg-gray-200 rounded animate-pulse" />
            ) : isEditing ? (
              <input
                type="text"
                name="personalEmail"
                value={formData.personalEmail}
                onChange={handleInputChange}
                placeholder="Enter Personal Email"
                className="w-full p-3 border border-gray-200 bg-gray-50 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-200 transition"
              />
            ) : (
              <span className="px-4 py-2 text-base font-medium text-gray-800 bg-gray-100 rounded w-full">
                {formData.personalEmail || "-"}
              </span>
            )}
          </div>
        </div>
      </div>
    </StudentLayout>
  );
};

export default MyProfile;
